import { DEFAULT_CONTACT, MISSING_FIELD_LIST } from '@/entities/rma';
import { BIOTRICITY_ADDRESS } from '@/shared/config';
import dayjs from 'dayjs';
import _ from 'lodash';

/**
 * Derives the initial shipping-label form state from an RMA ticket. Builds the sender contact from the ticket senderInformation contact over DEFAULT_CONTACT, uses BIOTRICITY_ADDRESS as the recipient, dates the shipment today, and lists MISSING_FIELD_LIST entries for an absent sender email, recipient email or recipient phone.
 *
 * @param rmaTicket - RMA ticket carrying the sender information.
 * @returns Initial shipment date, both contacts and the list of missing fields.
 */
const getDefaultState = (rmaTicket: any) => {
  const { senderInformation } = rmaTicket;
  const senderContact = {
    ...DEFAULT_CONTACT,
    name: senderInformation?.contact?.name,
    street1: senderInformation?.contact?.street1 ?? '',
    city: senderInformation?.contact?.city ?? '',
    country: senderInformation?.contact?.country ?? '',
    state: senderInformation?.contact?.state ?? '',
    zip: senderInformation?.contact?.zip ?? '',
    phone: senderInformation?.contact?.phone ?? '',
    email: senderInformation?.contact?.email ?? '',
  };

  const recipientContact = {
    ...DEFAULT_CONTACT,
    ...BIOTRICITY_ADDRESS,
  };

  const missingModalData = [];
  if (!senderContact.email) {
    missingModalData.push(MISSING_FIELD_LIST.SENDER_EMAIL);
  }
  if (!recipientContact.email) {
    missingModalData.push(MISSING_FIELD_LIST.RECIPIENT_EMAIL);
  }

  if (!recipientContact.phone) {
    missingModalData.push(MISSING_FIELD_LIST.RECIPIENT_PHONE);
  }

  return {
    shipmentDate: dayjs(),
    senderContact,
    recipientContact,
    missingModalData,
  };
};

/**
 * Determines which of the sender's RMA devices are not yet on a shipping label. Returns empty arrays for empty senderInformation, otherwise flattens shipmentDevices across all labels, filters senderInformation.devices to ids absent from that list and collects both their deviceId values and the device objects.
 *
 * @param senderInformation - Sender block holding devices and existing labels.
 * @returns Object with notInLabelDevices device ids and notInLabelDeviceObjects device records.
 */
const getNotInLabelDevices = (senderInformation: any): any => {
  if (_.isEmpty(senderInformation)) {
    return {
      notInLabelDevices: [],
      notInLabelDeviceObjects: [],
    };
  }

  const { labels } = senderInformation;
  /**
   * Collects every device already attached to one of the sender's existing shipping labels. Built by reducing labels and concatenating each label's shipmentDevices into a single flat array.
   *
   * @returns Flat array of devices already present on labels.
   */
  const inLabelDevices = labels.reduce(
    (array: any, label: any) => array.concat(label.shipmentDevices),
    [],
  );
  /**
   * Holds the sender's devices that appear on no existing shipping label. Produced by filtering senderInformation.devices to those whose id is not found in inLabelDevices.
   *
   * @returns Devices absent from all labels.
   */
  const filteredNotInLabelDevices = senderInformation.devices.filter(
    (device: any) => !inLabelDevices.find((inLabelDevice: any) => inLabelDevice.id === device.id),
  );
  const notInLabelDevices: any = [];
  const notInLabelDeviceObjects: any = [];
  filteredNotInLabelDevices.forEach((device: any) => {
    notInLabelDevices.push(device.deviceId);
    notInLabelDeviceObjects.push(device);
  });
  return {
    notInLabelDevices,
    notInLabelDeviceObjects,
  };
};

export { getDefaultState, getNotInLabelDevices };
